/**
 * What a device map is allowed to claim, as pure functions.
 *
 * The data has existed since the columns were added — `assets.location_lat`,
 * `location_lng`, `location_pin_status` — and nothing ever rendered it. The
 * reason a map is worth building is the reason it is easy to get wrong:
 *
 * ── Not every pin is a pin ────────────────────────────────────────────────
 *
 * `location_pin_status` is `'confirmed'` when a tech stood at the assembly and
 * placed it, and `'unset'` when quick-add defaulted the device to the SITE's
 * own coordinates. An unset "pin" is therefore not a location at all — it is
 * the building centroid, and on a campus every unset device stacks on the same
 * dot. Drawing those identically to confirmed ones would invent precision the
 * system does not have, which `product-doctrine.md` calls out by name.
 *
 * ── A map that shows 4 of 12 devices reads as complete ────────────────────
 *
 * That is the failure this module exists to prevent, and it is worse than
 * having no map: the office would count the pins and believe it. Devices with
 * no usable coordinates are counted and named, never silently dropped.
 */

export type PinConfidence = "confirmed" | "approximate" | "none";

export interface MappableDevice {
    id: string;
    name: string | null;
    serial_number?: string | null;
    location_lat?: number | string | null;
    location_lng?: number | string | null;
    location_pin_status?: string | null;
    next_due_date?: string | null;
}

/** A finite number, or null. Coordinates arrive from pg as strings. */
function coord(v: number | string | null | undefined): number | null {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : null;
}

/**
 * How much this device's position can be trusted.
 *
 * `0,0` is treated as NO pin, not a pin in the Gulf of Guinea — it is what a
 * null coordinate looks like after a careless import, and a device drawn 5,000
 * miles off the site would be read as a bug in the map rather than in the data.
 */
export function pinConfidence(d: MappableDevice): PinConfidence {
    const lat = coord(d.location_lat);
    const lng = coord(d.location_lng);
    if (lat === null || lng === null) return "none";
    if (lat === 0 && lng === 0) return "none";
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return "none";
    return d.location_pin_status === "confirmed" ? "confirmed" : "approximate";
}

export interface MapSplit {
    /** A tech placed this pin. Draw it solid. */
    confirmed: MappableDevice[];
    /** Site centroid stand-in. Draw it differently and say so. */
    approximate: MappableDevice[];
    /** No usable coordinates. Must be LISTED, never dropped. */
    unplaced: MappableDevice[];
}

export function splitByPin(devices: readonly MappableDevice[]): MapSplit {
    const out: MapSplit = { confirmed: [], approximate: [], unplaced: [] };
    for (const d of devices) {
        const c = pinConfidence(d);
        if (c === "confirmed") out.confirmed.push(d);
        else if (c === "approximate") out.approximate.push(d);
        else out.unplaced.push(d);
    }
    return out;
}

/**
 * The sentence under the map — what it is and is not showing.
 *
 * Always states the total. A map is a claim about a set, and the only honest
 * version names the size of that set before anything else.
 */
export function mapCoverageNote(split: MapSplit): string {
    const total = split.confirmed.length + split.approximate.length + split.unplaced.length;
    if (total === 0) return "No devices on record at this location yet.";

    const bits: string[] = [];
    if (split.confirmed.length > 0) {
        bits.push(`${split.confirmed.length} placed by a tech`);
    }
    if (split.approximate.length > 0) {
        // Say WHY it is approximate, or "approximate" reads as GPS drift
        // rather than "nobody has ever placed this one".
        bits.push(`${split.approximate.length} shown at the building — nobody has placed them yet`);
    }
    if (split.unplaced.length > 0) {
        bits.push(`${split.unplaced.length} with no location at all`);
    }
    return `${total} device${total === 1 ? "" : "s"}: ${bits.join(" · ")}.`;
}

/** Whether the map is worth rendering at all. */
export function hasAnythingToMap(split: MapSplit): boolean {
    return split.confirmed.length + split.approximate.length > 0;
}

/**
 * Centre and a rough zoom for the pins we do have.
 *
 * Falls back to the site's own coordinates, then to null — a caller with
 * neither should render the list, not a map of the ocean.
 */
export function mapCentre(
    split: MapSplit,
    siteFallback: { lat?: number | string | null; lng?: number | string | null } = {},
): { lat: number; lng: number } | null {
    const placed = [...split.confirmed, ...split.approximate];
    if (placed.length > 0) {
        let latSum = 0;
        let lngSum = 0;
        for (const d of placed) {
            latSum += coord(d.location_lat)!;
            lngSum += coord(d.location_lng)!;
        }
        return { lat: latSum / placed.length, lng: lngSum / placed.length };
    }
    const lat = coord(siteFallback.lat);
    const lng = coord(siteFallback.lng);
    return lat !== null && lng !== null ? { lat, lng } : null;
}

/** Compliance tone for a pin, matching the compliance card's vocabulary. */
export function pinTone(d: MappableDevice, today: Date): "overdue" | "missing_due" | "ok" {
    if (!d.next_due_date) return "missing_due";
    const due = new Date(`${d.next_due_date.slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(due.getTime())) return "missing_due";
    const now = new Date(`${today.toISOString().slice(0, 10)}T00:00:00Z`);
    return due < now ? "overdue" : "ok";
}
